import { displayDateUS, formatTimeAmPm } from "./dateUtils";
import { PATIENT_THEME_HEX } from "../pages/Patients/patientTheme";

const DEFAULT_LOGO = "/login-logo.png";

/** @param {string|null|undefined} s */
function escapeHtml(s) {
  if (s == null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * @param {string|null|undefined} officeLogo
 * @returns {string}
 */
export function resolveTenantLogoUrl(officeLogo) {
  if (typeof window === "undefined") return DEFAULT_LOGO;
  const origin = window.location.origin;
  if (!officeLogo) return `${origin}${DEFAULT_LOGO}`;
  if (officeLogo.startsWith("http")) return officeLogo;
  const path = officeLogo.startsWith("/") ? officeLogo : `/${officeLogo}`;
  return `${origin}${path}`;
}

/** Parse YYYY-MM-DD as local date */
function parseLocalDateParts(dateString) {
  if (!dateString) return null;
  const parts = String(dateString).split("T")[0].split("-").map(Number);
  if (parts.length < 3) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
}

/** Weekday + date only (no leading day number), e.g. "Thursday, Mar 19, 2026" */
function formatRowDate(dateString) {
  const date = parseLocalDateParts(dateString);
  if (!date) return "—";
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const monthShort = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  return `${weekday}, ${monthShort} ${day}, ${year}`;
}


function formatTime(isoString) {
  return formatTimeAmPm(isoString);
}

function formatBreaksPlain(breaks) {
  if (!breaks || breaks.length === 0) return "—";
  return breaks
    .map((brk) => {
      const range = `${formatTime(brk.breakInTime)} – ${formatTime(brk.breakOutTime)}`;
      const dur = brk.duration ? ` (${brk.duration})` : "";
      return `${range}${dur}`;
    })
    .join("; ");
}

/**
 * @param {object} params
 * @param {string|null} params.companyName
 * @param {string|null} params.officeLogo
 * @param {string} params.employeeName
 * @param {Date|string} params.periodStart
 * @param {Date|string} params.periodEnd
 * @param {object} params.summary
 * @param {Array} params.days
 * @returns {string}
 */
export function generateTimeCardPrintHTML({
  companyName,
  officeLogo,
  employeeName,
  periodStart,
  periodEnd,
  summary = {},
  days = [],
}) {
  const logoSrc = escapeHtml(resolveTenantLogoUrl(officeLogo));
  const safeCompany = escapeHtml(companyName || "Company");
  const safeEmployee = escapeHtml(employeeName || "—");
  const start =
    periodStart instanceof Date ? periodStart : new Date(periodStart);
  const end = periodEnd instanceof Date ? periodEnd : new Date(periodEnd);
  const periodLabel = `${displayDateUS(start) || "—"} – ${displayDateUS(end) || "—"}`;
  const generatedOn = escapeHtml(
    new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),
  );

  const totalWork = escapeHtml(summary.totalWorkTime || "00:00:00");
  const totalBreak = escapeHtml(summary.totalBreakTime || "00:00:00");
  const totalDays = escapeHtml(String(summary.totalDays ?? 0));
  const presentDays = escapeHtml(String(summary.presentDays ?? 0));

  const tableRows = [];

  days.forEach((day) => {
    const dateLine = formatRowDate(day.date);
    const sessions = day.sessions || [];
    const sessionCount = sessions.length;

    if (sessionCount === 0) {
      tableRows.push(`
        <tr>
          <td><div class="date-line">${escapeHtml(dateLine)}</div></td>
          <td>—</td>
          <td>—</td>
          <td>—</td>
          <td class="breaks-cell">—</td>
          <td class="col-duration">—</td>
        </tr>
      `);
      return;
    }

    sessions.forEach((session, idx) => {
      const breaksText = formatBreaksPlain(session.breaks || []);
      tableRows.push(`
        <tr>
          ${idx === 0 ? `<td class="date-cell" rowspan="${sessionCount}"><div class="date-line">${escapeHtml(dateLine)}</div></td>` : ""}
          <td class="cell-tight">S${idx + 1}</td>
          <td class="cell-tight tabular">${escapeHtml(formatTime(session.clockInTime))}</td>
          <td class="cell-tight tabular">${session.clockOutTime ? escapeHtml(formatTime(session.clockOutTime)) : '<span class="muted">Active</span>'}</td>
          <td class="breaks-cell">${escapeHtml(breaksText)}</td>
          <td class="col-duration tabular">${escapeHtml(session.totalWorkTime || "00:00:00")}</td>
        </tr>
      `);
    });

    if (sessionCount > 1) {
      tableRows.push(`
        <tr class="day-total-row">
          <td colspan="6">
            <span class="day-total-label">Day total (${sessionCount} sessions)</span>
            <span class="day-total-stat">Work: <strong>${escapeHtml(day.dayTotalWorkTime || "00:00:00")}</strong></span>
            <span class="day-total-stat">Break: <strong>${escapeHtml(day.dayTotalBreakTime || "00:00:00")}</strong></span>
          </td>
        </tr>
      `);
    }
  });

  const rowsHtml = tableRows.join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Time card — ${safeEmployee}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 8mm 10mm;
    }
    * { box-sizing: border-box; }
    body {
      font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      margin: 0;
      padding: 0;
      color: #1e293b;
      background: #fff;
      font-size: 14px;
      line-height: 1.5;
      font-weight: 400;
    }
    .print-toolbar {
      display: flex;
      gap: 8px;
      justify-content: center;
      padding: 12px 16px;
      background: ${PATIENT_THEME_HEX.soft};
      border-bottom: 1px solid ${PATIENT_THEME_HEX.border};
    }
    .print-toolbar button {
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid ${PATIENT_THEME_HEX.border};
      background: #fff;
      color: ${PATIENT_THEME_HEX.text};
    }
    .print-toolbar button.primary {
      background: ${PATIENT_THEME_HEX.strong};
      color: #fff;
      border-color: ${PATIENT_THEME_HEX.strong};
    }
    .print-toolbar button:hover { filter: brightness(0.97); }
    .report-wrapper {
      max-width: 920px;
      margin: 0 auto;
      padding: 28px 32px 36px;
    }
    .title-block {
      width: 100%;
      border: 1px solid ${PATIENT_THEME_HEX.border};
      background: ${PATIENT_THEME_HEX.soft};
      border-collapse: collapse;
      margin-bottom: 4px;
      border-radius: 8px;
      overflow: hidden;
    }
    .title-block td { vertical-align: middle; padding: 14px 18px; }
    .title-block .logo-cell { width: 1%; white-space: nowrap; }
    .title-block .logo-cell img {
      max-height: 48px; width: auto; display: block; object-fit: contain;
    }
    .company-name {
      font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
      color: ${PATIENT_THEME_HEX.text}; margin: 0 0 2px 0;
    }
    .doc-title {
      font-size: 22px; font-weight: 800; color: ${PATIENT_THEME_HEX.title}; margin: 0;
    }
    .sub {
      text-align: right; font-size: 12px; font-weight: 400; color: #64748b; margin: 0 0 12px 0;
    }
    .header-rule {
      height: 2px; background: ${PATIENT_THEME_HEX.strong}; margin: 0 0 16px 0;
    }
    .details-box {
      background: ${PATIENT_THEME_HEX.soft};
      border: 1px solid ${PATIENT_THEME_HEX.border};
      border-radius: 8px;
      padding: 14px 18px;
      margin-bottom: 16px;
    }
    .details-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px 24px;
      font-size: 13.5px;
    }
    .details-grid .label { font-weight: 700; color: #475569; }
    .details-grid .value { color: #1e293b; font-weight: 400; }
    .stats-row {
      display: flex; flex-wrap: wrap; gap: 16px; margin-top: 10px;
      padding-top: 10px; border-top: 1px solid ${PATIENT_THEME_HEX.border};
      font-size: 13.5px;
      font-weight: 400;
    }
    .stats-row span { color: #475569; }
    .stats-row strong { color: ${PATIENT_THEME_HEX.title}; font-family: ui-monospace, monospace; font-weight: 400; }
    .stats-row .work { color: ${PATIENT_THEME_HEX.strong}; }
    .stats-row .break { color: #d97706; }
    .items-table {
      width: 100%; table-layout: fixed; border-collapse: collapse;
      border: 1px solid ${PATIENT_THEME_HEX.border};
      font-size: 13px;
    }
    .items-table col.col-date { width: 16%; }
    .items-table col.col-session { width: 7%; }
    .items-table col.col-in { width: 14%; }
    .items-table col.col-out { width: 14%; }
    .items-table col.col-breaks { width: 31%; }
    .items-table col.col-duration { width: 18%; }
    .items-table th {
      background: ${PATIENT_THEME_HEX.soft};
      color: ${PATIENT_THEME_HEX.title};
      font-weight: 400;
      text-align: left;
      padding: 8px 6px;
      font-size: 12px;
      border-bottom: 1px solid ${PATIENT_THEME_HEX.border};
    }
    .items-table th.col-duration, .items-table td.col-duration { text-align: right; }
    .items-table td {
      border-bottom: 1px solid #e2e8f0;
      padding: 6px;
      vertical-align: top;
    }
    .items-table tbody tr:nth-child(even) { background: #fafbff; }
    .items-table .breaks-cell {
      word-break: break-word; overflow-wrap: anywhere;
    }
    .date-line { font-size: 13px; line-height: 1.4; font-weight: 400; }
    .date-cell { border-right: 1px solid #eef2ff; }
    .cell-tight { font-size: 13px; line-height: 1.45; font-weight: 400; }
    .tabular { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 13px; font-weight: 400; }
    .muted { color: #64748b; font-style: italic; font-weight: 400; }
    .breaks-cell { font-weight: 400; }
    .day-total-row td {
      background: ${PATIENT_THEME_HEX.soft} !important;
      padding: 5px 8px !important;
      font-size: 12.5px;
      font-weight: 400;
      color: #475569;
    }
    .day-total-label { font-weight: 400; margin-right: 12px; }
    .day-total-stat { margin-right: 14px; }
    .day-total-stat strong { font-family: ui-monospace, monospace; color: ${PATIENT_THEME_HEX.strong}; font-weight: 400; }
    .totals-box {
      margin-top: 14px; padding: 12px 16px;
      background: ${PATIENT_THEME_HEX.soft};
      border: 1px solid ${PATIENT_THEME_HEX.border};
      border-radius: 8px;
      font-size: 14px;
      font-weight: 400;
    }
    .totals-box .meta { display: flex; flex-wrap: wrap; gap: 20px; }
    .totals-box .meta span { color: #475569; }
    .totals-box .meta strong { color: ${PATIENT_THEME_HEX.title}; font-family: ui-monospace, monospace; font-weight: 400; }
    .footer {
      margin-top: 24px; padding-top: 14px; text-align: center;
      border-top: 1px solid ${PATIENT_THEME_HEX.border};
      font-size: 13px; font-weight: 400; color: #64748b;
    }
    @media print {
      html, body {
        width: 100%;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      body { padding: 0; font-weight: 400; }
      .no-print { display: none !important; }
      .report-wrapper { max-width: 100%; width: 100%; margin: 0; padding: 0; }
      .company-name, .doc-title, .details-grid .label { font-weight: 700; }
    }
  </style>
</head>
<body>
  <div class="no-print print-toolbar">
    <button type="button" class="primary" onclick="window.print()">Print</button>
    <button type="button" onclick="window.close()">Close</button>
  </div>
  <div class="report-wrapper">
    <div class="header">
      <table class="title-block" cellpadding="0" cellspacing="0">
        <tr>
          <td class="logo-cell">
            <img src="${logoSrc}" alt="Company logo" />
          </td>
          <td>
            <p class="company-name">${safeCompany}</p>
            <h1 class="doc-title">Time card report</h1>
          </td>
        </tr>
      </table>
      <p class="sub">Generated on ${generatedOn}</p>
      <div class="header-rule"></div>
    </div>

    <div class="details-box">
      <div class="details-grid">
        <div><span class="label">Employee: </span><span class="value">${safeEmployee}</span></div>
        <div><span class="label">Pay period: </span><span class="value">${escapeHtml(periodLabel)}</span></div>
        <div><span class="label">Total days: </span><span class="value">${totalDays}</span></div>
        <div><span class="label">Present days: </span><span class="value">${presentDays}</span></div>
      </div>
      <div class="stats-row">
        <span>Total work: <strong class="work">${totalWork}</strong></span>
        <span>Total break: <strong class="break">${totalBreak}</strong></span>
      </div>
    </div>

    <table class="items-table">
      <colgroup>
        <col class="col-date" />
        <col class="col-session" />
        <col class="col-in" />
        <col class="col-out" />
        <col class="col-breaks" />
        <col class="col-duration" />
      </colgroup>
      <thead>
        <tr>
          <th>Date</th>
          <th>Session</th>
          <th>Clock in</th>
          <th>Clock out</th>
          <th>Breaks</th>
          <th class="col-duration">Work duration</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="6" style="text-align:center;padding:20px;color:#64748b;">No activity for this period.</td></tr>`}
      </tbody>
    </table>

    <div class="totals-box">
      <div class="meta">
        <span><strong>Period work total:</strong> ${totalWork}</span>
        <span><strong>Period break total:</strong> ${totalBreak}</span>
      </div>
    </div>

    <div class="footer">
      <p style="margin:0;">${safeCompany}</p>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Opens a new window with the time card HTML (print-ready).
 * @param {string} html
 */
export function openTimeCardPrintWindow(html) {
  const printWindow = window.open("", "_blank");
  if (!printWindow || !printWindow.document) {
    // eslint-disable-next-line no-alert
    alert("Please allow popups to view the time card report.");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}
